import {
  BuildingIcon,
  Button,
  EntityCard,
  ResourceIcon,
  Tabs,
  ViewHeader,
  formatAmount,
  formatRate,
} from '@spacegame/ui';
import { BUILDINGS, BUILDING_KEYS, type BuildingKey } from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { buildingCard, type BuildingOutput } from '../game/selectors';
import { entityArt } from '../components/EntityArt';

/**
 * "What this building gives me now, and what the next level would give."
 *
 * The owner asked for the production rate to be visible on the card itself,
 * and the delta is the number the decision actually turns on — a rate in
 * isolation does not tell you whether the upgrade in front of you is worth it.
 * Both figures come from the same report the top bar uses, so energy
 * throttling, talents and event buffs are already folded in.
 */
function OutputStat({ output }: { output: BuildingOutput }) {
  const delta = output.next - output.current;
  const unit = output.kind === 'energy' ? 'MW' : output.kind === 'storage' ? '' : '/h';
  const format = (value: number) =>
    output.kind === 'production' ? formatRate(value) : `${formatAmount(value)}${unit ? ' ' + unit : ''}`;

  return (
    <div className="rounded-control border border-edge-subtle bg-surface-2 px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 font-display text-label-sm uppercase text-content-muted">
          {output.resource !== 'power' ? <ResourceIcon resource={output.resource} size={14} /> : null}
          {output.kind === 'storage' ? 'Kapazität' : output.kind === 'energy' ? 'Energie' : 'Förderung'}
        </span>
        <span data-sg-numeric className="text-xs font-semibold text-content">
          {format(output.current)}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="font-display text-label-sm uppercase text-content-muted">
          Nächste Stufe
        </span>
        <span data-sg-numeric className="text-xs font-semibold text-success-bright">
          {output.kind === 'production' ? '' : delta >= 0 ? '+' : ''}
          {format(delta)}
        </span>
      </div>
      {output.energyDelta ? (
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="font-display text-label-sm uppercase text-content-muted">
            Energiebedarf
          </span>
          <span data-sg-numeric className="text-xs font-semibold text-warning-bright">
            +{formatAmount(output.energyDelta)} MW
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function BuildingsView() {
  const { game, clock, activePlanetIndex } = useGame();
  const actions = useGameActions();
  const now = clock();
  // Damage/repair and moon buildings stay home-planet-only (see the doc
  // comments on `upgradeBuilding`/`applyCompletion` in the engine) — only
  // building CONSTRUCTION on the planetary catalogue below is planet-aware.
  const planetIndex = activePlanetIndex;
  const planet = game.planets[planetIndex]!;

  // The Subraum-Phalanx stands on the base moon (GDD §3), so the catalogue is
  // split by location rather than shown as one undifferentiated grid.
  const planetKeys = BUILDING_KEYS.filter((k) => BUILDINGS[k].location !== 'moon');
  const moonKeys = BUILDING_KEYS.filter((k) => BUILDINGS[k].location === 'moon');

  const renderCard = (key: BuildingKey, keyPlanetIndex: number) => {
    const card = buildingCard(game, key, now, keyPlanetIndex);
    return (
      <EntityCard
        key={key}
        name={card.label}
        description={card.description}
        art={entityArt(BUILDINGS[key].sprite, card.label)}
        icon={<BuildingIcon size={40} />}
        level={card.level}
        state={card.state}
        stats={card.output ? <OutputStat output={card.output} /> : undefined}
        cost={card.cost}
        available={game.resources}
        duration={card.durationSeconds}
        requirement={card.requirement}
        actionLabel={card.level === 0 ? 'Bauen' : 'Ausbauen'}
        onAction={
          card.state === 'active'
            ? undefined
            : () =>
                card.state === 'damaged'
                  ? actions.repairBuilding(key)
                  : actions.upgradeBuilding(key, keyPlanetIndex)
        }
        secondaryAction={
          card.state === 'active' ? (
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              disabled={!card.canQueueMore}
              onClick={() => actions.upgradeBuilding(key, keyPlanetIndex)}
            >
              Stufe {card.nextLevel} anhängen
            </Button>
          ) : undefined
        }
      />
    );
  };

  return (
    <>
      {game.planets.length > 1 ? (
        <Tabs
          variant="enclosed"
          className="mb-3"
          value={String(planetIndex)}
          onChange={(id) => actions.setActivePlanetIndex(Number(id))}
          items={game.planets.map((p, i) => ({
            id: String(i),
            label: i === 0 ? `${p.name} (Heimat)` : p.name,
          }))}
        />
      ) : null}
      <ViewHeader
        eyebrow={planet.name}
        title="Planetare Gebäude"
        description={
          planetIndex === 0
            ? 'Jedes Gebäude skaliert in Baukosten und Effizienz nach festen mathematischen Funktionen.'
            : 'Kolonie-Baustufe — läuft unabhängig von der Heimatwelt, ohne deren Naniten-Bautempo-Bonus.'
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {planetKeys.map((key) => renderCard(key, planetIndex))}
      </div>

      <ViewHeader
        eyebrow={game.moon.name}
        title="Mond-Anlagen"
        description="Der unzerstörbare Basis-Mond trägt die Subraum-Phalanx — Grundlage für Frühwarnung und Push-Benachrichtigungen."
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {moonKeys.map((key) => renderCard(key, 0))}
      </div>
    </>
  );
}
